
import Page from '@/Page.vue';
import { Component, Prop, PropSync, Ref, Watch } from 'vue-property-decorator';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import {
    CategoryModel,
    FindingFileModel,
    FindingModel,
    ImplementationStateEnum,
    MeasureModel,
    PlanItemModel,
    ResponseBasicModel,
    ResponseModel,
    TenantUserModel,
    EscalationGroupTypeEnum,
    ProcessTypeEnum,
} from '@/libs/Api';
import WorkTaskFindingItemMeasure from './WorkTaskFindingItemMeasure.vue';
import { cloneDeep } from 'lodash';
import { measuresStore } from '@/libs/measures/+state/store';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import WorkTaskDialogFiles from '../components/WorkTaskDialogFiles.vue';
import WorkTaskEscalation from '@/libs/work-tasks/components/WorkTaskEscalation.vue';
import { Guid } from '@/libs/common/functions/guid';
import { formatDate } from '@/libs/common/functions/format-date';

@Component({
    components: {
        WorkTaskEscalation,
        WorkTaskFindingItemMeasure,
        WorkTaskDialogFiles,
    },
})
export default class WorkTaskVerifyMeasureItem extends Page {
    /* Component props */
    @PropSync('measure') item!: MeasureModel;
    @Prop() category!: CategoryModel;
    @Prop() finding!: FindingModel;

    @Ref() form!: any;

    protected EscalationGroupTypeEnum: typeof EscalationGroupTypeEnum = EscalationGroupTypeEnum;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;

    /* Auth variables */
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    /* Component state variables */
    dialog = false;
    dialogReworkValid = false;
    dialogReturn = false;
    valid = true;
    saving = false;
    hasChanges = true;
    dialogModel: MeasureModel | null = null;

    get storedPlanItem(): PlanItemModel {
        return planItemsStore.useGetter(CrudGetter.Detail, this.item.finding?.response?.planItemId) as PlanItemModel;
    }

    get canEditVerification(): boolean {
        return this.item.measureVerificationUsers!.any((n) => n.tenantUserId == this.tenantUser.id);
    }

    get response(): ResponseBasicModel {
        return this.finding.response!;
    }

    get findingFiles(): FindingFileModel[] {
        return this.finding?.findingFiles ?? [];
    }

    get storedResponse(): ResponseModel | null {
        const planItem = planItemsStore.useGetter(CrudGetter.Detail, this.item.finding?.response?.planItemId) as PlanItemModel;
        return planItem?.responses?.firstOrDefault((x) => x.id == this.finding.responseId) ?? null;
    }

    get isMobile(): boolean {
        return this.$vuetify.breakpoint.width < 576;
    }

    /* Action functions */

    openDialog() {
        this.dialogModel = cloneDeep(this.item);
        this.dialog = true;
    }

    closeDialog() {
        this.dialog = false;
    }

    markVerified() {
        this.saving = true;
        const dialogModel = cloneDeep(this.dialogModel);
        dialogModel!.implementationState = ImplementationStateEnum.Verified;
        dialogModel!.verificationUserId = this.tenantUser.id;
        measuresStore.dispatch(CrudAction.Update, { item: dialogModel });
        this.subscribe(measuresStore, CrudReponse.Update)
            .then((e) => {
                this.saving = false;
                this.$emit('planItemChanged');
                this.closeDialog();
            })
            .catch((e) => {
                this.saving = false;
                console.log(e);
            });
    }

    returnToImplementation() {
        if (this.form.validate()) {
            this.saving = true;
            this.dialogReturn = false;
            const dialogModel = cloneDeep(this.dialogModel);
            dialogModel!.implementationState = ImplementationStateEnum.New;
            dialogModel!.isPerfomedWhenCreated = false;
            dialogModel!.perfomedWhenCreatedAt = null;
            dialogModel!.perfomedWhenCreatedByUserId = null;
            dialogModel!.isVerifiedWhenCreated = false;
            dialogModel!.verifiedWhenCreatedAt = null;
            dialogModel!.verifiedWhenCreatedByUserId = null;
            measuresStore.dispatch(CrudAction.Update, { item: dialogModel });
            this.subscribe(measuresStore, CrudReponse.Update)
                .then((e) => {
                    this.saving = false;
                    this.$emit('planItemChanged');
                    this.closeDialog();
                })
                .catch((e) => {
                    this.saving = false;
                    console.log(e);
                });
        }
    }

    protected getImplementationTenantUserList(measure: MeasureModel): string[] {
        return measure?.measureImplementationUsers?.map((x) => x.tenantUserId!) ?? [];
    }

    protected setImplementationTenantUserList(measure: MeasureModel, tenantUsers: string[]) {
        if (!measure) {
            return;
        }
        if (!measure.measureImplementationUsers) {
            measure.measureImplementationUsers = [];
        }
        const existingTenantUsers = measure.measureImplementationUsers.map((x) => x.tenantUserId!);
        const tenantUsersToAdd = tenantUsers.filter((x) => !existingTenantUsers.contains(x));
        const tenantUsersToDelete = existingTenantUsers.filter((x) => !tenantUsers.contains(x));
        for (const tenantUser of tenantUsersToAdd) {
            measure.measureImplementationUsers.push({
                id: Guid.EmptyGuid(),
                tenantUserId: tenantUser,
            });
        }
        for (const tenantUser of tenantUsersToDelete) {
            const implementationUser = measure.measureImplementationUsers.find((x) => x.tenantUserId! == tenantUser)!;
            measure.measureImplementationUsers.splice(measure.measureImplementationUsers.indexOf(implementationUser), 1);
        }
    }

    protected getResponseValueLabel(responseValue: number): string | null {
        return this.category?.responseValues?.find((x) => x.value == responseValue)?.label ?? null;
    }
}
